"use client"
import { useState, useEffect } from "react"
import { X, Cookie, ChevronUp, ChevronDown } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"

export default function CookieBanner() {
  const [isOpen, setIsOpen] = useState(false)
  const [isMounted, setIsMounted] = useState(false)
  const [hasUserChoice, setHasUserChoice] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  })

  useEffect(() => {
    setIsMounted(true)

    const saved = localStorage.getItem("cookie-preferences")
    
    if (saved) {
      // Si déjà un choix → on ferme le panneau
      const parsed = JSON.parse(saved)
      setPreferences(parsed)
      setHasUserChoice(true);
      setIsOpen(false)
    } else {
      // PAS de choix précédent → on ouvre par défaut
      setHasUserChoice(false);
      setIsOpen(true)
    }
  }, [])

  // ─── Actions ───────────────────────────────────────────────────────────
  const handleAcceptAll = () => {
    const all = { necessary: true, analytics: true, marketing: true };
    localStorage.setItem("cookie-preferences", JSON.stringify(all));
    setPreferences(all);
    setHasUserChoice(true);
    setIsOpen(false);
    console.log("Tous les cookies acceptés !");
  };

  const handleRejectAll = () => {
    const minimal = { necessary: true, analytics: false, marketing: false };
    localStorage.setItem("cookie-preferences", JSON.stringify(minimal));
    setPreferences(minimal);
    setHasUserChoice(true);
    setIsOpen(false);
    console.log("Cookies non-essentiels refusés.");
  };

  const handleSavePreferences = () => {
    localStorage.setItem("cookie-preferences", JSON.stringify(preferences));
    setHasUserChoice(true);
    setIsOpen(false);
    console.log("Préférences cookies enregistrées.");
  };

  const handleCloseWithoutSave = () => {
    setIsOpen(false);
    // Ne rien enregistrer → panneau réapparaîtra au prochain clic sur le bouton flottant
  };

  const togglePreference = (key) => {
    // On ne peut pas désactiver "necessary"
    if (key === "necessary") return;
    
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (!isMounted) return null

  return (
    <div className="fixed bottom-[1.5rem] left-4 z-50">
      {/* Bouton rond flottant */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className="relative w-14 h-14 rounded-full bg-gradient-to-br from-[#f29f2f] to-[#f29f2f] text-white shadow-lg hover:shadow-xl transition-shadow duration-200 flex items-center justify-center group"
        aria-label={isOpen ? "Fermer les préférences cookies" : "Ouvrir les préférences cookies"}
      >
        <Cookie className="w-6 h-6 text-[#003479]" />
        {/* Point de notification animé (optionnel si déjà ouvert) */}
        <motion.div
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute top-0 right-0 w-3 h-3 bg-[#0879bd] rounded-full"
        />
      </motion.button>

      {/* Panneau principal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3 }}
            className="absolute bottom-20 left-0 w-[32rem] max-w-[calc(100vw-32px)] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
          >
            {/* En-tête */}
            <div className="bg-gradient-to-r from-[#003479] to-[#0879bd] text-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Cookie className="w-5 h-5 text-[#f29f2f]" />
                <h3 className="text-lg font-semibold">Gestion des Cookies</h3>
              </div>
              <motion.button
                whileHover={{ rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-white/20 rounded-full transition-colors"
                aria-label="Fermer"
              >
                <X className="w-5 h-5" />
              </motion.button>
            </div>

            {/* Contenu */}
            <div className="p-4 space-y-4 max-h-96 overflow-y-auto">
              <p className="text-sm text-gray-700 leading-relaxed">
                Nous utilisons des cookies pour améliorer votre expérience, analyser le trafic et personnaliser le contenu et les publicités.
              </p>

              <div className="space-y-3 border-y border-gray-200 py-3">
                {/* Nécessaires – toujours activé */}
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">Cookies Nécessaires</div>
                    <p className="text-xs text-gray-500">Essentiels au fonctionnement du site</p>
                  </div>
                  <div className="relative inline-flex h-6 w-11 items-center rounded-full bg-[#58bceb]">
                    <span className="inline-block h-4 w-4 transform rounded-full bg-white transition translate-x-0.5" />
                  </div>
                </div>

                {/* Analytiques */}
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <label className="text-sm font-medium text-gray-900 cursor-pointer">Cookies Analytiques</label>
                    <p className="text-xs text-gray-500">Mesure d’audience et statistiques</p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => togglePreference("analytics")}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      preferences.analytics ? "bg-[#58bceb]" : "bg-gray-300"
                    }`}
                  >
                    <motion.span
                      animate={{ x: preferences.analytics ? 20 : 4 }}
                      className="inline-block h-4 w-4 transform rounded-full bg-white"
                    />
                  </motion.button>
                </div>

                {/* Marketing */}
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <label className="text-sm font-medium text-gray-900 cursor-pointer">Cookies Marketing</label>
                    <p className="text-xs text-gray-500">Publicités personnalisées</p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => togglePreference("marketing")}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      preferences.marketing ? "bg-[#58bceb]" : "bg-gray-300"
                    }`}
                  >
                    <motion.span
                      animate={{ x: preferences.marketing ? 20 : 4 }}
                      className="inline-block h-4 w-4 transform rounded-full bg-white"
                    />
                  </motion.button>
                </div>
              </div>
            </div>

            {/* Boutons d’action */}
            <div className="bg-gray-50 p-4 space-y-2 border-t border-gray-200">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleRejectAll}
                className="w-full py-2.5 px-4 rounded-lg text-sm font-medium border-2 border-gray-400 hover:border-gray-500 text-gray-800 transition-colors"
              >
                Refuser tout
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleAcceptAll}
                className="w-full py-2.5 px-4 rounded-lg text-sm font-medium bg-gradient-to-r from-[#003479] to-[#0879bd] hover:brightness-110 text-white transition-all"
              >
                Accepter tout
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleSavePreferences}
                className="w-full py-2.5 px-4 rounded-lg text-sm font-medium bg-[#f29f2f] hover:bg-[#e68a1f] text-white transition-colors"
              >
                Enregistrer mes choix
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}